/**
 * Upgrade-modal context object, event name, and `useUpgradeModal` hook.
 *
 * `.ts` (no JSX) so `UpgradeModalProvider.tsx` stays a Fast Refresh component boundary.
 */

import { createContext, useContext } from 'react';

/** Custom event name dispatched by the apiClient 402 interceptor. */
export const UPGRADE_REQUIRED_EVENT = 'upgrade:required';

export interface UpgradeModalContextValue {
  /** Whether the upgrade modal is currently open. */
  upgradeOpened: boolean;
  /** Imperatively open the modal with an optional reason message. */
  openUpgrade: (reason?: string) => void;
  /**
   * Register a one-shot callback to run when the upgrade modal closes.
   * Returns an unregister function for cleanup. The callback is automatically
   * removed after it fires once (or when the caller unmounts and calls unregister).
   *
   * Used by components that need to retry an action after subscription
   * (e.g. ReviewFlowerDraftModal retries flower order creation).
   */
  registerOnClose: (cb: () => void) => () => void;
}

/**
 * React context object. Exported for `UpgradeModalProvider` only — consumers use `useUpgradeModal`.
 */
export const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

/**
 * Hook to access the global upgrade modal.
 * Must be used within an UpgradeModalProvider.
 */
export const useUpgradeModal = (): UpgradeModalContextValue => {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  }
  return ctx;
};
